import { useState, useMemo } from 'react';

import * as dateFns from 'date-fns';
import { datesForRange } from 'src/utils/rollup';
import useDebounce from 'src/hooks/use-debounce';

export default function useSegments(fullRange, retirementInfo) {
  const [state, setState] = useState({
    period: 'month',
    dateRange: {
      startDate: fullRange?.start || dateFns.startOfYear(new Date()),
      endDate: fullRange?.end || dateFns.endOfYear(new Date()),
    },
  });

  // Debounce the date range state with a 50ms delay
  const debouncedState = useDebounce(state ?? {}, 50);

  // Calculate segments based on the selected period and debounced date range
  const segments = useMemo(
    () =>
      datesForRange(
        debouncedState.period,
        debouncedState.dateRange?.startDate,
        debouncedState.dateRange?.endDate,
        retirementInfo
      ),
    [debouncedState, retirementInfo]
  );

  // automatically optimize forecast chart rendering by rolling up to year
  const handleSetDateRange = (range, period) => {
    const result = { ...state };
    // if a period is provided, update the period
    if (period && period !== state.period) result.period = period;

    // if a range is provided, update the date range
    if (range) {
      result.dateRange = range;

      // if the date range is invalid, set it to the current year
      const difference = Math.abs(dateFns.differenceInYears(range.startDate, range.endDate));
      if (Number.isNaN(difference)) {
        console.error('invalid date range');
        const now = new Date();
        result.dateRange = { startDate: dateFns.startOfYear(now), endDate: dateFns.endOfYear(now) };
      }

      // Automatically optimize forecast chart rendering by rolling up to year
      // if (process.env.REACT_APP_ENV !== 'development') {
      if (difference === 10 && state.period !== 'quarter') result.period = 'quarter';
      if (difference > 10 && state.period !== 'year') result.period = 'year';
      if (difference <= 4 && state.period !== 'month') result.period = 'month';
      // }
    }

    // update the state
    setState((prev) => ({ ...prev, ...result }));
  };

  // Return relevant values and functions as part of the hook's interface
  return {
    segments,
    setPeriod: (period) => handleSetDateRange(null, period),
    setDateRange: handleSetDateRange,
    period: debouncedState.period,
    dateRange: debouncedState.dateRange,
    debouncedRange: debouncedState.dateRange,
  };
}
